import { IFurniture } from '@/editor/data/model/furniture';
import FurnitureEntity from '@/editor/entities/furnitureEntity';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { FurnitureAbsorbManager } from '@/editor/manager/absorb/box/furnitureAbsorbManager';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { VIRTUAL_ID_SUFFIX } from '@/editor/controller/helper/constants';
import { OperateAbstract } from './operateAbstract';
import { cloneDeep } from 'lodash-es';

export class OperateFurniture extends OperateAbstract<EEntityType.Furniture> {
  declare checkedEntity: FurnitureEntity | null | undefined;

  private _furnitureAbsorbManager = new FurnitureAbsorbManager();
  private _selectedBoxes = new OperateSelectedBoxes();

  private _virtualFurnitureData: IFurniture | null = null;
  private _virtualFurnitureEntity: FurnitureEntity | null = null;

  private _clickedOffset: IPoint2d = { x: 0, y: 0 };

  constructor(_gs: TGlobalStores) {
    super(_gs);
  }

  override startMove(point: IPoint2d) {
    if (!this.checkedEntity) return false;

    this.moving = false;

    const furnitureData = this._gs.getDataByIdType(this.checkedEntity.id, EEntityType.Furniture);
    if (!furnitureData) return false;

    this._clickedOffset = Point2dUtils.subtracted(furnitureData.center, point);
    this._furnitureAbsorbManager.updateData({ excludeFurnitureIds: [this.checkedEntity.id] });

    this.checkedEntity.setVisible(false);
    this._virtualFurnitureData = cloneDeep(furnitureData);
    this._virtualFurnitureEntity = new FurnitureEntity({ id: this._virtualFurnitureData.id + VIRTUAL_ID_SUFFIX });
    this._virtualFurnitureEntity.update(this._virtualFurnitureData);

    this.updateBoxes();

    return true;
  }

  move(point: IPoint2d) {
    if (!this.checkedEntity || !this._virtualFurnitureData) return;

    this.moving = true;

    let center = Point2dUtils.added(point, this._clickedOffset);

    const absorbResult = this._furnitureAbsorbManager.calculateOnly({
      center,
      width: this._virtualFurnitureData.width,
      thickness: this._virtualFurnitureData.thickness,
      rotation: this._virtualFurnitureData.rotation
    });
    if (absorbResult) {
      center = absorbResult.absorbPoint;
      this._virtualFurnitureData.rotation = absorbResult.rotation;
    }

    this._virtualFurnitureData.center = center;
    this._virtualFurnitureEntity?.update(this._virtualFurnitureData);
    this.updateBoxes({ x: center.x, y: center.y, rotation: this._virtualFurnitureData.rotation });
  }

  override moveByOffset(offset: IPoint2d) {
    if (!this.checkedEntity || !this._virtualFurnitureData) return;

    this.moving = true;

    const center = Point2dUtils.added(this._virtualFurnitureData.center, offset);
    this._virtualFurnitureData.center = center;

    this._virtualFurnitureEntity?.update(this._virtualFurnitureData);
    this.updateBoxes({ x: center.x, y: center.y, rotation: this._virtualFurnitureData.rotation });
  }

  completeMove(_point: IPoint2d) {
    if (this.moving && this.checkedEntity && this._virtualFurnitureData) {
      this._gs.furnitureOperation.modifyFurniture(this.checkedEntity.id, {
        center: this._virtualFurnitureData.center,
        rotation: this._virtualFurnitureData.rotation
      });
    }

    this.moving = false;
    this.checkedEntity?.setVisible(true);
    this._virtualFurnitureData = null;
  }

  clear() {
    this.checkedEntity?.setVisible(true);
    this._furnitureAbsorbManager.clearModel();
    this._selectedBoxes.clear();
    this._virtualFurnitureData = null;
    this._virtualFurnitureEntity?.dispose();
    this._virtualFurnitureEntity = null;
    this.checkedEntity = null;
    this.moving = false;
    this._clickedOffset = { x: 0, y: 0 };
  }

  dispose() {
    this.clear();
    this._furnitureAbsorbManager.dispose();
    this._selectedBoxes.dispose();
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this.checkedEntity) return;

    this._selectedBoxes.update({ type: EEntityType.Furniture, entityId: this.checkedEntity.id }, params);
  }
}
